import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) headers.set("x-auth-token", token);
    return headers;
  },
});

export const logisticsApi = createApi({
  reducerPath: "logisticsApi",
  baseQuery,
  tagTypes: ["Trips", "Expenses", "Materials", "Licenses", "Documents", "Routes", "Sensors", "Reminders"],

  endpoints: (builder) => ({

    // =======================
    // ✅ TRIPS
    // =======================
    addTrip: builder.mutation({
      query: (body) => ({
        url: "/logistics/trips",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Trips"],
    }),

    getTrips: builder.query({
      query: () => "/logistics/trips",
      providesTags: ["Trips"],
    }),

    updateTrip: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/logistics/trips/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Trips"],
    }),

    startTrip: builder.mutation({
      query: (id) => ({
        url: `/logistics/trips/${id}/start`,
        method: "PUT",
      }),
      invalidatesTags: ["Trips"],
    }),

    completeTrip: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/logistics/trips/${id}/complete`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Trips"],
    }),

    cancelTrip: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/logistics/trips/${id}/cancel`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Trips"],
    }),

    deleteTrip: builder.mutation({
      query: (id) => ({
        url: `/logistics/trips/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Trips"],
    }),

    // =======================
    // ✅ EXPENSES
    // =======================
    addExpense: builder.mutation({
      query: (body) => ({
        url: "/logistics/expenses",
        method: "POST",
        body, // FormData supported
      }),
      invalidatesTags: ["Expenses"],
    }),

    getExpenses: builder.query({
      query: () => "/logistics/expenses",
      providesTags: ["Expenses"],
    }),

    updateExpense: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/logistics/expenses/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Expenses"],
    }),

    deleteExpense: builder.mutation({
      query: (id) => ({
        url: `/logistics/expenses/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Expenses"],
    }),

    // =======================
    // ✅ MATERIALS
    // =======================
    addMaterial: builder.mutation({
      query: (body) => ({
        url: "/logistics/materials",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Materials"],
    }),

    getMaterials: builder.query({
      query: () => "/logistics/materials",
      providesTags: ["Materials"],
    }),

    updateMaterial: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/logistics/materials/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Materials"],
    }),

    deleteMaterial: builder.mutation({
      query: (id) => ({
        url: `/logistics/materials/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Materials"],
    }),

    // =======================
    // ✅ LICENSES
    // =======================
    addLicense: builder.mutation({
      query: (body) => ({
        url: "/logistics/licenses",
        method: "POST",
        body, // FormData
      }),
      invalidatesTags: ["Licenses"],
    }),

    getLicenses: builder.query({
      query: () => "/logistics/licenses",
      providesTags: ["Licenses"],
    }),

    getLicenseRecipients: builder.query({
      query: () => "/logistics/licenses/recipients",
      providesTags: ["Licenses"],
      transformResponse: (response) => response?.data || [],
    }),

    updateLicense: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/logistics/licenses/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Licenses"],
    }),

    deleteLicense: builder.mutation({
      query: (id) => ({
        url: `/logistics/licenses/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Licenses"],
    }),

    // =======================
    // ✅ DOCUMENTS
    // =======================
    addDocument: builder.mutation({
      query: (body) => ({
        url: "/logistics/documents",
        method: "POST",
        body, // FormData
      }),
      invalidatesTags: ["Documents"],
    }),

    getDocuments: builder.query({
      query: () => "/logistics/documents",
      providesTags: ["Documents"],
    }),

    updateDocument: builder.mutation({
      query: ({ id, body }) => ({
        url: `/logistics/documents/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Documents"],
    }),

    deleteDocument: builder.mutation({
      query: (id) => ({
        url: `/logistics/documents/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Documents"],
    }),

    // =======================
    // ✅ ROUTES
    // =======================
    getRoutes: builder.query({
      query: () => "/logistics/routes",
      providesTags: ["Routes"],
    }),

    addRoute: builder.mutation({
      query: (body) => ({
        url: "/logistics/routes",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Routes"],
    }),

    updateRoute: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/logistics/routes/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Routes"],
    }),

    deleteRoute: builder.mutation({
      query: (id) => ({
        url: `/logistics/routes/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Routes"],
    }),

    // =======================
    // ✅ SENSORS
    // =======================
getSensors: builder.query({
      query: () => "/sensors",
      providesTags: ["Sensors"],
      transformResponse: (response) => {
        // Backend returns { success: true, data: [...] }
        return response?.data || [];
      }
    }),

    addSensor: builder.mutation({
      query: (body) => ({
        url: "/sensors",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Sensors"],
      transformResponse: (response) => response?.data
    }),

    updateSensor: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/sensors/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Sensors"],
      transformResponse: (response) => response?.data
    }),

    deleteSensor: builder.mutation({
      query: (id) => ({
        url: `/sensors/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Sensors"],
    }),

    assignSensor: builder.mutation({
      query: ({ sensorId, userId = null, deviceId = null }) => ({
        url: `/sensors/${sensorId}/assign`,
        method: "PUT",
        body: {
          userId,
          deviceId
        },
      }),
      invalidatesTags: ["Sensors"],
    }),


    addReminder: builder.mutation({
      query: (body) => ({
        url: "/reminders",
        method: "POST",
        body
      }),
      invalidatesTags: ["Reminders"],
      transformResponse: (response) => response?.data
    }),

    getReminders: builder.query({
      query: () => "/reminders",
      providesTags: ["Reminders"],
      transformResponse: (response) => {
        // Backend returns { success: true, data: [...] }
        return response?.data || [];
      }
    }),

 addDriver: builder.mutation({
  query: (body) => ({
    url: "/logistics/drivers",   // 👈 backend route jo humne banaya tha
    method: "POST",
    body,
  }),
  invalidatesTags: ["Drivers"],
}),


    // ✅ CLIENTS - FIXED ENDPOINTS
    // =======================
    addClient: builder.mutation({
      query: (body) => ({
        url: "/clients",  // ✅ FIXED: Changed from /logisticsclient/clients
        method: "POST",
        body,
      }),
      invalidatesTags: ["Clients"],
    }),

    getClients: builder.query({
      query: () => "/clients",  // ✅ FIXED
      providesTags: ["Clients"],
    }),

    updateClient: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/clients/${id}`,  // ✅ FIXED
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Clients"],
    }),

    deleteClient: builder.mutation({
      query: (id) => ({
        url: `/clients/${id}`,  // ✅ FIXED
        method: "DELETE",
      }),
      invalidatesTags: ["Clients"],
    }),

deleteReminder: builder.mutation({
  query: (id) => ({
    url: `/reminders/${id}`,
    method: "DELETE",
  }),
  invalidatesTags: ["Reminders"],
}),

  }),
});

// =======================
// ✅ EXPORT HOOKS (CLEAN)
// =======================
export const {
  useAddTripMutation,
  useGetTripsQuery,
  useUpdateTripMutation,
  useStartTripMutation,
  useCompleteTripMutation,
  useCancelTripMutation,
  useDeleteTripMutation,

  useAddExpenseMutation,
  useGetExpensesQuery,
  useUpdateExpenseMutation,
  useDeleteExpenseMutation,

  useAddMaterialMutation,
  useGetMaterialsQuery,
  useUpdateMaterialMutation,
  useDeleteMaterialMutation,

  useAddLicenseMutation,
  useGetLicensesQuery,
  useGetLicenseRecipientsQuery,
  useUpdateLicenseMutation,
  useDeleteLicenseMutation,

  useAddDocumentMutation,
  useGetDocumentsQuery,
  useUpdateDocumentMutation,
  useDeleteDocumentMutation,

  useGetRoutesQuery,
  useAddRouteMutation,
  useUpdateRouteMutation,
  useDeleteRouteMutation,

  useGetSensorsQuery,
  useAddSensorMutation,
  useDeleteSensorMutation,
  useAddReminderMutation,
useGetRemindersQuery,
useAssignSensorMutation,
useAddDriverMutation,

useAddClientMutation,
useGetClientsQuery,
useUpdateClientMutation,
useDeleteClientMutation,
useUpdateSensorMutation,
useDeleteReminderMutation

} = logisticsApi;
